import { useEffect, createContext, useContext, useState, useCallback } from "react";
import { logout } from "../../utils/logout";

interface User {
    userId?: string;
    name?: string;
    userName?: string;
    email?: string;
    role: {
        _id?: string;
        name?: string;
        permissions?: string[]
    }
}

interface AuthContextType {
    user: User | null;
    permissions: string[];
    hasPermission: (permission: string) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
    hasAllPermissions: (permissions: string[]) => boolean;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (userData: User, token: string) => void;
    logOut: () => void;
    refreshUser: () => void;
    can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        console.error('useAuth must be used within AuthProvider')
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadUser = () => {
            try {
                const userStr = localStorage.getItem('loginUser');
                const token = localStorage.getItem('accessToken');
                if (userStr && token) {
                    const userData = JSON.parse(userStr);
                    setUser(userData);
                    setPermissions(userData.role?.permissions || []);
                }
            } catch (error) {
                console.error('Failed to load user from localstorage :', error)
            } finally {
                setIsLoading(false)
            }
        }
        loadUser();
    }, [])

    const login = useCallback((userData: User, token: string) => {
        setUser(userData);
        setPermissions(userData.role?.permissions || []);
        setIsLoading(false);
    }, []);

    const hasPermission = useCallback((permission: string): boolean => {
        if (!permission.length) return false;

        if (permissions.includes(permission)) return true;

        const wildcard = permission.split(':')[0] + ':*';
        if (permissions.includes(wildcard)) return true;

        if (user?.role?.name?.toLowerCase() === 'admin') return true;

        return false;
    }, [permissions, user])

    const hasAnyPermission = useCallback((permList: string[]): boolean => {
        return permList.some(permission => hasPermission(permission));
    }, [hasPermission])

    const hasAllPermissions = useCallback((permList: string[]): boolean => {
        return permList.every(permission => hasPermission(permission));
    }, [hasPermission])

    const logOut = logout;

    const can = useCallback((permission: string): boolean => {
        return hasPermission(permission);
    }, [hasPermission]);

    const refreshUser = useCallback(() => {
        const userStr = localStorage.getItem('loginUser');
        if (userStr) {
            const userData = JSON.parse(userStr);
            setUser(userData);
            setPermissions(userData.role?.permissions || [])
        }
    }, [])

    const isAuthenticated = !!user && !!localStorage.getItem('accessToken');

    return (
        <AuthContext.Provider value={{ user, permissions, hasPermission, hasAnyPermission, hasAllPermissions, isAuthenticated, isLoading, logOut, refreshUser, can, login }}>{children}</AuthContext.Provider>
    )

}